import React, { useEffect, useState, useCallback } from 'react';
import supabase from '../../supabase';
import TrackingPreview from './TrackingPreview';
import InvoiceTable from './InvoiceTable';
import RecurringInvoicesPopup from './RecurringInvoicesPopup';
import InvoiceHeader from './InvoiceHeader';
import { useInvoiceOperations } from './useInvoiceOperations';
import './ClientInvoiceTracker.css';

const InvoiceManagement = ({
  onCreateInvoice,
  onSwitchToCreateTab,
  sectorStatus,
  selectedInvoiceData,
  onClearSelection
}) => {
  const [user, setUser] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [clients, setClients] = useState({});
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [loading, setLoading] = useState(false);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('All');
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [userProfile, setUserProfile] = useState(null);
  const [fromInfo, setFromInfo] = useState(null);
  const [recurringChildren, setRecurringChildren] = useState([]);
  const [selectedRecurringInvoice, setSelectedRecurringInvoice] = useState(null);
  const [showRecurringPopup, setShowRecurringPopup] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [sendingReminder, setSendingReminder] = useState({});
  const [sendingInvoice, setSendingInvoice] = useState({});
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);

  // Tax and invoice format options - these will be set from the selected invoice data
  const [taxType, setTaxType] = useState('NONE');
  const [gstRate, setGstRate] = useState(18);
  const [cgstRate, setCgstRate] = useState(9);
  const [sgstRate, setSgstRate] = useState(9);
  const [igstRate, setIgstRate] = useState(18);
  const [hasdiscount, setHasdiscount] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [showHsnColumn, setShowHsnColumn] = useState(false);
  const [showValueColumn, setShowValueColumn] = useState(false);
  const [showTaxPerItemColumn, setShowTaxPerItemColumn] = useState(true);

  // PDF Viewer key state for fixing "Eo is not a function" error
  const [pdfViewerKey, setPdfViewerKey] = useState(0);

  const filterOptions = ['All', 'Paid', 'Unpaid', 'Overdue', 'Recurring'];

  // Use custom hook for invoice operations
  const {
    sendInvoiceEmail,
    sendReminder,
    handleStatusUpdate,
    deleteInvoice,
    startEditInvoice,
    cancelEditInvoice,
    saveEditInvoice
  } = useInvoiceOperations({
    user,
    clients,
    invoices,
    setInvoices,
    setRecurringChildren,
    setSendingInvoice,
    setSendingReminder,
    setUpdatingStatus,
    setEditingInvoice,
    setEditFormData,
    userProfile,
    editingInvoice,
    editFormData
  });

  // Force PDFViewer remount when selected invoice data changes (for TrackingPreview)
  useEffect(() => {
    if (selectedInvoice && viewMode === 'preview') {
      setPdfViewerKey(k => k + 1);
    }
  }, [selectedInvoice, viewMode, userProfile, fromInfo]);

  // Handle selected invoice data from Clients component
  useEffect(() => {
    if (selectedInvoiceData && selectedInvoiceData.invoice) {
      handleInvoiceClick(selectedInvoiceData.invoice);
      if (onClearSelection) onClearSelection();
    }
  }, [selectedInvoiceData]);

  useEffect(() => {
    if (sectorStatus) {
      setStatusFilter(sectorStatus);
    }
  }, [sectorStatus]);

  // Fetch all data required for preview and table
  useEffect(() => {
    const fetchUserAndData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      if (!user) return;

      const [
        { data: profile },
        { data: invoiceList },
        { data: itemList },
        { data: clientList },
        { data: bankList }
      ] = await Promise.all([
        supabase.from('users').select('*').eq('id', user.id).single(),
        supabase.from('invoices').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('invoice_items').select('*').eq('user_id', user.id),
        supabase.from('clients').select('*').eq('user_id', user.id),
        supabase.from('banks').select('*').eq('user_id', user.id),
      ]);

      setUserProfile(profile);
      setFromInfo(profile);
      setInvoices(invoiceList || []);
      setInvoiceItems(itemList || []);
      if (clientList) {
        const clientMap = {};
        clientList.forEach(c => clientMap[c.id] = c.client_name);
        setClients(clientMap);
      }
      setBanks(bankList || []);
      if (bankList && bankList.length > 0) setSelectedBank(bankList[0]);
    };
    fetchUserAndData();
  }, []);

// Filter invoices by status and search
// Filter invoices by status and search
  useEffect(() => {
    let filtered = [...invoices];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(inv =>
        (inv.invoice_number || '').toLowerCase().includes(q) ||
        (clients[inv.client_id] || '').toLowerCase().includes(q) ||
        (inv.status || 'Unpaid').toLowerCase().includes(q)
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(inv => {
        const st = inv.status || 'Unpaid';
        
        // Always hide Draft invoices from non-All filters
        if (st === 'Draft') {
          return false;
        }
        
        if (statusFilter === 'Overdue') {
          const today = new Date();
          const due = new Date(inv.due_date);
          today.setHours(0,0,0,0);
          due.setHours(0,0,0,0);
          return st === 'Unpaid' && due < today;
        }
        
        if (statusFilter === 'Recurring') {
          return inv.is_recurring === true;
        }
        
        // For Paid, Unpaid, or any other specific status
        return st === statusFilter;
      });
    }
    
    setFilteredInvoices(filtered);
  }, [searchQuery, statusFilter, invoices, clients]);
  
  // Helper: get items for invoice
  const getInvoiceItemsByInvoiceId = useCallback((invoiceId) => {
    return invoiceItems.filter(item => item.invoice_id === invoiceId);
  }, [invoiceItems]);

  // Helper: calculate summary for an invoice
  // ✅ FIXED: Now reads discount from the actual invoice data
  const calculateSummary = useCallback((items, invoiceDiscount = 0) => {
    let subtotal = 0, totalTax = 0, sgstTotal = 0, cgstTotal = 0, igstTotal = 0;
    items.forEach(item => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.rate_per_item || item.unit_price || 0) || 0;
      const base = qty * price;
      subtotal += base;
      
      // Calculate tax based on item's tax percentages
      const sgstPercent = Number(item.sgst_percent) || 0;
      const cgstPercent = Number(item.cgst_percent) || 0;
      
      if (sgstPercent > 0 || cgstPercent > 0) {
        const sgst = base * (sgstPercent / 100);
        const cgst = base * (cgstPercent / 100);
        sgstTotal += sgst;
        cgstTotal += cgst;
        totalTax += sgst + cgst;
      }
    });
    
    let Discount = invoiceDiscount || 0;
    let total = subtotal + totalTax;
    
    if (Discount > 0) {
      // Apply discount to subtotal
      const subtotalAfterDiscount = subtotal - Discount;
      
      // Recalculate taxes on discounted amount
      sgstTotal = 0;
      cgstTotal = 0;
      igstTotal = 0;
      totalTax = 0;
      
      items.forEach(item => {
        const qty = Number(item.quantity) || 0;
        const price = Number(item.rate_per_item || item.unit_price || 0) || 0;
        const itemBase = qty * price;
        const itemRatio = itemBase / subtotal; // Proportion of this item
        const itemDiscountedBase = itemBase - (Discount * itemRatio);
        
        const sgstPercent = Number(item.sgst_percent) || 0;
        const cgstPercent = Number(item.cgst_percent) || 0;
        
        if (sgstPercent > 0 || cgstPercent > 0) {
          const sgst = itemDiscountedBase * (sgstPercent / 100);
          const cgst = itemDiscountedBase * (cgstPercent / 100);
          sgstTotal += sgst;
          cgstTotal += cgst;
          totalTax += sgst + cgst;
        }
      });
      
      total = subtotalAfterDiscount + totalTax;
    }
    
    return { subtotal, totalTax, total, sgstTotal, cgstTotal, igstTotal, Discount, discountAmount: Discount };
  }, []);

  // ✅ FIXED: Now passes the invoice's discount to calculateSummary
  const calculateSummaryByInvoice = useCallback((invoiceId) => {
    const items = getInvoiceItemsByInvoiceId(invoiceId);
    const invoice = invoices.find(inv => inv.id === invoiceId);
    const invoiceDiscount = Number(invoice?.discount) || 0;
    return calculateSummary(items, invoiceDiscount);
  }, [getInvoiceItemsByInvoiceId, calculateSummary, invoices]);

  // Item-wise total for PDF
  const getLocalItemTotal = useCallback((item) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.rate_per_item || item.unit_price || 0) || 0;
    return qty * price;
  }, []);

  // Tax per item for PDF
  const calculateLocalItemTax = useCallback((item) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.rate_per_item || item.unit_price || 0) || 0;
    const base = qty * price;
    
    const sgstPercent = Number(item.sgst_percent) || 0;
    const cgstPercent = Number(item.cgst_percent) || 0;
    
    return base * ((sgstPercent + cgstPercent) / 100);
  }, []);

  // Overdue check
  const isInvoiceOverdue = (invoice) => {
    if (!invoice.due_date || invoice.status === 'Paid') return false;
    const today = new Date();
    const due = new Date(invoice.due_date);
    today.setHours(0,0,0,0);
    due.setHours(0,0,0,0);
    return due < today && (invoice.status === 'Unpaid' || !invoice.status);
  };

  // Fetch recurring children
  const fetchRecurringChildren = async (parentInvoiceId) => {
    setLoadingChildren(true);
    try {
      const { data: childInvoices, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('parent_invoice_id', parentInvoiceId)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setRecurringChildren(childInvoices || []);
    } catch (error) {
      console.error('Error fetching recurring children:', error);
      setRecurringChildren([]);
    } finally {
      setLoadingChildren(false);
    }
  };

  const handleRecurringInvoiceClick = async (invoice) => {
    setSelectedRecurringInvoice(invoice);
    setShowRecurringPopup(true);
    await fetchRecurringChildren(invoice.id);
  };

  const closeRecurringPopup = () => {
    setShowRecurringPopup(false);
    setSelectedRecurringInvoice(null);
    setRecurringChildren([]);
  };

  // Invoice click handler (both for preview and recurring)
  const handleInvoiceClick = async (invoice) => {
    if (invoice.is_recurring && statusFilter === 'Recurring') {
      handleRecurringInvoiceClick(invoice);
      return;
    }
    setLoading(true);
    try {
      const [{ data: invData }, { data: itemsData }, { data: clientData }] = await Promise.all([
        supabase.from('invoices').select('*').eq('id', invoice.id).single(),
        supabase.from('invoice_items').select('*').eq('invoice_id', invoice.id).order('id'),
        supabase.from('clients').select('*').eq('id', invoice.client_id).single(),
      ]);

      // ✅ FIXED: Set discount state from invoice data
      const invoiceDiscount = Number(invData?.discount) || 0;
      setDiscount(invoiceDiscount);
      setHasdiscount(invoiceDiscount > 0);

      const transformed = {
        storeName: invData.company_name || '',
        fromName: invData.from_name || '',
        fromAddress: invData.from_address || '',
        fromPhone: invData.from_phone || '',
        fromEmail: invData.from_email || '',
        fromBusinessNumber: invData.business_number || '',
        logo: invData.logo_url || '',
        invoiceNumber: invData.invoice_number || '',
        invoiceDate: invData.issue_date || '',
        dueDate: invData.due_date || '',
        toName: clientData.client_name || '',
        toAddress: clientData.address || '',
        toPhone: clientData.phone || '',
        toMobile: clientData.mobile || '',
        toFax: clientData.fax || '',
        toEmail: clientData.email || '',
        clientId: invoice.client_id,
        discount: invoiceDiscount, // ✅ Add discount to transformed data
        items: (itemsData || []).map(item => ({
          name: item.name || '',
          description: item.description || '',
          hsn_sac: item.hsn_sac || '',
          quantity: item.quantity || 0,
          rate: item.rate_per_item || 0,
          sgst_percent: item.sgst_percent || 0,
          cgst_percent: item.cgst_percent || 0,
          total_gst_percent: item.total_gst_percent || 0,
          total_tax: item.total_tax || 0,
          total: item.total || 0
        })),
        taxRate: invData.tax_rate || 0,
        notes: invData.notes || ''
      };

      setSelectedInvoice(transformed);
      setViewMode('preview');
    } catch (err) {
      console.error('Error loading invoice detail:', err);
      alert('Error loading invoice details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Invoice totals for preview
  const calculateSubtotal = () => selectedInvoice?.items?.reduce((sum, i) => sum + (parseFloat(i.rate) * parseFloat(i.quantity)), 0) || 0;
  const calculateTax = () => selectedInvoice?.items?.reduce((sum, i) => sum + parseFloat(i.total_tax), 0) || 0;
  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = calculateTax();
    const invoiceDiscount = Number(selectedInvoice?.discount) || 0;
    return subtotal + tax - invoiceDiscount;
  };

  const handleCreateInvoiceClick = () => {
    if (onSwitchToCreateTab) {
      onSwitchToCreateTab();
    } else if (onCreateInvoice) {
      onCreateInvoice();
    }
  };

  // Safe status update
  const safeHandleStatusUpdate = async (invoiceId, newStatus) => {
    try {
      if (!Array.isArray(invoices)) {
        console.error('Invoices array is not available');
        return;
      }
      await handleStatusUpdate(invoiceId, newStatus);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating invoice status. Please try again.');
    }
  };

  // Preview mode render
  if (viewMode === 'preview' && selectedInvoice) {
    return (
      <TrackingPreview
        formData={selectedInvoice}
        onBack={() => { setViewMode('list'); setSelectedInvoice(null); }}
        calculateSubtotal={calculateSubtotal}
        calculateTax={calculateTax}
        calculateTotal={calculateTotal}
        pdfViewerKey={pdfViewerKey} // Pass the key to TrackingPreview
      />
    );
  }

  return (
    <div className="Invoice-tracking-invoice-management-container">
      <InvoiceHeader
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isSearchOpen={isSearchOpen}
        setIsSearchOpen={setIsSearchOpen}
        statusFilter={statusFilter}
        filterOptions={filterOptions}
        onCreateInvoice={handleCreateInvoiceClick}
      />

      <div className="Invoice-tracking-filter-tabs">
        {filterOptions.map(opt => (
          <button
            key={opt}
            className={`Invoice-tracking-filter-tab ${statusFilter === opt ? 'Invoice-tracking-filter-tab-active' : ''}`}
            onClick={() => setStatusFilter(opt)}
          >
            {opt}
          </button>
        ))}
      </div>

      {loading && (
        <div className="Invoice-tracking-loading-overlay">
          <div className="Invoice-tracking-content">
            <div className="Invoice-tracking-loading-spinner" />
            <p className="Invoice-tracking-loading-text">Loading...</p>
          </div>
        </div>
      )}

      <InvoiceTable
        filteredInvoices={filteredInvoices}
        clients={clients}
        statusFilter={statusFilter}
        editingInvoice={editingInvoice}
        editFormData={editFormData}
        setEditFormData={setEditFormData}
        updatingStatus={updatingStatus}
        sendingInvoice={sendingInvoice}
        sendingReminder={sendingReminder}
        isInvoiceOverdue={isInvoiceOverdue}
										   
        onStatusUpdate={safeHandleStatusUpdate}
        onSendInvoiceEmail={sendInvoiceEmail}
        onSendReminder={sendReminder}
        onStartEdit={startEditInvoice}
        onSaveEdit={saveEditInvoice}
        onCancelEdit={cancelEditInvoice}
        onDeleteInvoice={deleteInvoice}
        onCreateInvoice={handleCreateInvoiceClick}
        searchQuery={searchQuery}
        isSearchOpen={isSearchOpen}
        fromInfo={fromInfo}
        userProfile={userProfile}
        getInvoiceItemsByInvoiceId={getInvoiceItemsByInvoiceId}
        calculateSummaryByInvoice={calculateSummaryByInvoice}
        getLocalItemTotal={getLocalItemTotal}
        calculateLocalItemTax={calculateLocalItemTax}
        taxType={taxType}
        sgstRate={sgstRate}
        cgstRate={cgstRate}
        igstRate={igstRate}
        hasdiscount={hasdiscount}
        discount={discount}
        showHsnColumn={showHsnColumn}
        showValueColumn={showValueColumn}
        showTaxPerItemColumn={showTaxPerItemColumn}
        selectedBank={banks.length > 0 ? banks[0] : null}
        qrCodeDataURL={qrCode}
      />

      <RecurringInvoicesPopup
        isVisible={showRecurringPopup}
									 
        selectedInvoice={selectedRecurringInvoice}
						 
        recurringChildren={recurringChildren}
        loading={loadingChildren}
        onClose={closeRecurringPopup}
								   
										 
									   
									   
										 
										   
        onViewChild={(child) => {
          closeRecurringPopup();
          handleInvoiceClick(child);
        }}
        onStatusUpdate={safeHandleStatusUpdate}
        onSendInvoiceEmail={sendInvoiceEmail}
        onSendReminder={sendReminder}
        onStartEdit={startEditInvoice}
        onSaveEdit={saveEditInvoice}
        onCancelEdit={cancelEditInvoice}
        onDeleteInvoice={deleteInvoice}
      />
    </div>
  );
};

export default InvoiceManagement;